import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface FormData {
  // Contact Information
  fullName: string;
  email: string;
  
  // Programs to Advertise
  programsToAdvertise: string[];
  topPriorityProgram: string;
  paymentPriority: string;
  
  // Patient Intake Focus
  primaryPatientType: string;
  typicalAgeRange: string;
  admissionUrgency: string;
  firstCallMaker: string;
  
  // Geography & Availability
  targetAreas: string;
  phoneHours: string;
  staffed24_7: string;
  adsOutsideHours: string;
  
  // Budget & Success Criteria
  monthlyBudget: string;
  primaryGoal: string;
  successCriteria: string;
  
  // Compliance Safeguards
  phrasesToAvoid: string;
  legitScriptStatus: string;
}

export default function OasisAdsForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    programsToAdvertise: [],
    topPriorityProgram: "",
    paymentPriority: "",
    primaryPatientType: "",
    typicalAgeRange: "",
    admissionUrgency: "",
    firstCallMaker: "",
    targetAreas: "",
    phoneHours: "",
    staffed24_7: "",
    adsOutsideHours: "",
    monthlyBudget: "",
    primaryGoal: "",
    successCriteria: "",
    phrasesToAvoid: "",
    legitScriptStatus: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const programOptions = [
    "Medical detox",
    "Residential treatment",
    "PHP",
    "IOP",
    "Outpatient",
    "Dual diagnosis",
    "Alcohol treatment",
    "Drug treatment",
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    // Required fields
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (formData.programsToAdvertise.length === 0) {
      newErrors.programsToAdvertise = "Please select at least one program";
    }

    if (!formData.topPriorityProgram) {
      newErrors.topPriorityProgram = "Please select your top priority program";
    }

    if (!formData.paymentPriority) {
      newErrors.paymentPriority = "Please select payment priority";
    }

    if (!formData.primaryPatientType) {
      newErrors.primaryPatientType = "Please select primary patient type";
    }

    if (!formData.typicalAgeRange) {
      newErrors.typicalAgeRange = "Please select typical age range";
    }

    if (!formData.admissionUrgency) {
      newErrors.admissionUrgency = "Please select admission urgency";
    }

    if (!formData.firstCallMaker) {
      newErrors.firstCallMaker = "Please select who usually makes the first call";
    }

    if (!formData.targetAreas) {
      newErrors.targetAreas = "Please select target areas";
    }

    if (!formData.phoneHours.trim()) {
      newErrors.phoneHours = "Please provide phone hours";
    }

    if (!formData.staffed24_7) {
      newErrors.staffed24_7 = "Please indicate if staffed 24/7";
    }

    if (!formData.adsOutsideHours) {
      newErrors.adsOutsideHours = "Please indicate ad preference for outside hours";
    }

    if (!formData.monthlyBudget) {
      newErrors.monthlyBudget = "Please select monthly budget range";
    }

    if (!formData.primaryGoal) {
      newErrors.primaryGoal = "Please select primary campaign goal";
    }

    if (!formData.successCriteria.trim()) {
      newErrors.successCriteria = "Please describe success criteria";
    }

    if (!formData.legitScriptStatus) {
      newErrors.legitScriptStatus = "Please indicate LegitScript status";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProgramToggle = (program: string) => {
    setFormData((prev) => ({
      ...prev,
      programsToAdvertise: prev.programsToAdvertise.includes(program)
        ? prev.programsToAdvertise.filter((p) => p !== program)
        : [...prev.programsToAdvertise, program],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const emailData = {
        to: "mitch@mosheli.com",
        subject: "New Google Ads Intake Form - Oasis Recovery Center",
        formType: "Google Ads Intake",
        formData: {
          ...formData,
          programsToAdvertise: formData.programsToAdvertise.join(", "),
          submittedAt: new Date().toISOString(),
        },
      };

      // Using the same webhook as the existing contact form for consistency
      const response = await fetch("https://services.leadconnectorhq.com/hooks/oNgjqmOcyIwtcMEEfqUj/webhook-trigger/dec6dd5e-f521-4e1a-9845-6c8707289ad0", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(emailData),
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success("Form submitted successfully! We'll be in touch soon.");
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Something went wrong. Please try again or email us directly at mitch@mosheli.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center max-w-2xl mx-auto">
        <CheckCircle2 className="w-20 h-20 text-green-500 mb-6" />
        <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
        <p className="text-muted-foreground mb-4">
          Your Google Ads intake form has been submitted successfully.
        </p>
        <p className="text-sm text-muted-foreground">
          We'll review your information and get back to you within 24 hours to discuss your campaign strategy.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-2">Google Ads Intake Form</h1>
        <h2 className="text-xl text-primary font-semibold mb-4">Oasis Recovery Center</h2>
        <p className="text-muted-foreground">
          This form takes 5-8 minutes to complete and will help us create an effective Google Ads campaign for your treatment facility.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Contact Information */}
        <section className="space-y-4">
          <h3 className="text-xl font-semibold border-b pb-2">Contact Information</h3>
          
          <div>
            <Label htmlFor="fullName">
              Full Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="fullName"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              placeholder="Your full name"
              className={errors.fullName ? "border-destructive" : ""}
            />
            {errors.fullName && <p className="text-sm text-destructive mt-1">{errors.fullName}</p>}
          </div>

          <div>
            <Label htmlFor="email">
              Email Address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@oasisrecovery.com"
              className={errors.email ? "border-destructive" : ""}
            />
            {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
          </div>
        </section>

        {/* Programs to Advertise */}
        <section className="space-y-4">
          <h3 className="text-xl font-semibold border-b pb-2">Programs to Advertise</h3>
          
          <div>
            <Label>
              Which programs should we actively advertise? <span className="text-destructive">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {programOptions.map((program) => (
                <div key={program} className="flex items-center space-x-2">
                  <Checkbox
                    id={program}
                    checked={formData.programsToAdvertise.includes(program)}
                    onCheckedChange={() => handleProgramToggle(program)}
                  />
                  <label
                    htmlFor={program}
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {program}
                  </label>
                </div>
              ))}
            </div>
            {errors.programsToAdvertise && (
              <p className="text-sm text-destructive mt-1">{errors.programsToAdvertise}</p>
            )}
          </div>

          <div>
            <Label htmlFor="topPriorityProgram">
              Which ONE program is the top priority right now? <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.topPriorityProgram}
              onValueChange={(value) => setFormData({ ...formData, topPriorityProgram: value })}
            >
              <SelectTrigger className={errors.topPriorityProgram ? "border-destructive" : ""}>
                <SelectValue placeholder="Select top priority program" />
              </SelectTrigger>
              <SelectContent>
                {programOptions.map((program) => (
                  <SelectItem key={program} value={program}>
                    {program}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.topPriorityProgram && <p className="text-sm text-destructive mt-1">{errors.topPriorityProgram}</p>}
          </div>

          <div>
            <Label>
              Are you currently prioritizing self-pay, insurance, or both? <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={formData.paymentPriority}
              onValueChange={(value) => setFormData({ ...formData, paymentPriority: value })}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="self-pay" id="self-pay" />
                <Label htmlFor="self-pay">Self-pay</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="insurance" id="insurance" />
                <Label htmlFor="insurance">Insurance</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="both" id="both" />
                <Label htmlFor="both">Both</Label>
              </div>
            </RadioGroup>
            {errors.paymentPriority && <p className="text-sm text-destructive mt-1">{errors.paymentPriority}</p>}
          </div>
        </section>

        {/* Patient Intake Focus */}
        <section className="space-y-4">
          <h3 className="text-xl font-semibold border-b pb-2">Patient Intake Focus</h3>
          
          <div>
            <Label>
              Primary patient type <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={formData.primaryPatientType}
              onValueChange={(value) => setFormData({ ...formData, primaryPatientType: value })}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="adult-male" id="adult-male" />
                <Label htmlFor="adult-male">Adult male</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="adult-female" id="adult-female" />
                <Label htmlFor="adult-female">Adult female</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="co-ed" id="co-ed" />
                <Label htmlFor="co-ed">Co-ed</Label>
              </div>
            </RadioGroup>
            {errors.primaryPatientType && <p className="text-sm text-destructive mt-1">{errors.primaryPatientType}</p>}
          </div>

          <div>
            <Label htmlFor="typicalAgeRange">
              Typical age range <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.typicalAgeRange}
              onValueChange={(value) => setFormData({ ...formData, typicalAgeRange: value })}
            >
              <SelectTrigger className={errors.typicalAgeRange ? "border-destructive" : ""}>
                <SelectValue placeholder="Select age range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="18-25">18-25</SelectItem>
                <SelectItem value="26-35">26-35</SelectItem>
                <SelectItem value="36-45">36-45</SelectItem>
                <SelectItem value="46-55">46-55</SelectItem>
                <SelectItem value="55+">55+</SelectItem>
                <SelectItem value="all-ages">All ages</SelectItem>
              </SelectContent>
            </Select>
            {errors.typicalAgeRange && <p className="text-sm text-destructive mt-1">{errors.typicalAgeRange}</p>}
          </div>

          <div>
            <Label>
              Are most admissions urgent or planned? <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={formData.admissionUrgency}
              onValueChange={(value) => setFormData({ ...formData, admissionUrgency: value })}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="urgent" id="urgent" />
                <Label htmlFor="urgent">Urgent</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="planned" id="planned" />
                <Label htmlFor="planned">Planned</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="mixed" id="mixed" />
                <Label htmlFor="mixed">Mixed</Label>
              </div>
            </RadioGroup>
            {errors.admissionUrgency && <p className="text-sm text-destructive mt-1">{errors.admissionUrgency}</p>}
          </div>

          <div>
            <Label>
              Who usually makes the first call? <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={formData.firstCallMaker}
              onValueChange={(value) => setFormData({ ...formData, firstCallMaker: value })}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="patient" id="patient" />
                <Label htmlFor="patient">Patient</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="family-member" id="family-member" />
                <Label htmlFor="family-member">Family member</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="both-caller-types" id="both-caller-types" />
                <Label htmlFor="both-caller-types">Both</Label>
              </div>
            </RadioGroup>
            {errors.firstCallMaker && <p className="text-sm text-destructive mt-1">{errors.firstCallMaker}</p>}
          </div>
        </section>

        {/* Geography & Availability */}
        <section className="space-y-4">
          <h3 className="text-xl font-semibold border-b pb-2">Geography & Availability</h3>
          
          <div>
            <Label>
              Areas to target <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={formData.targetAreas}
              onValueChange={(value) => setFormData({ ...formData, targetAreas: value })}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="fort-myers-only" id="fort-myers-only" />
                <Label htmlFor="fort-myers-only">Fort Myers only</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="southwest-florida" id="southwest-florida" />
                <Label htmlFor="southwest-florida">Southwest Florida</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="florida-statewide" id="florida-statewide" />
                <Label htmlFor="florida-statewide">Florida statewide</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="national" id="national" />
                <Label htmlFor="national">National</Label>
              </div>
            </RadioGroup>
            {errors.targetAreas && <p className="text-sm text-destructive mt-1">{errors.targetAreas}</p>}
          </div>

          <div>
            <Label htmlFor="phoneHours">
              Admissions phone hours <span className="text-destructive">*</span>
            </Label>
            <Input
              id="phoneHours"
              value={formData.phoneHours}
              onChange={(e) => setFormData({ ...formData, phoneHours: e.target.value })}
              placeholder="e.g., 8 AM - 8 PM EST, Monday - Friday"
              className={errors.phoneHours ? "border-destructive" : ""}
            />
            {errors.phoneHours && <p className="text-sm text-destructive mt-1">{errors.phoneHours}</p>}
          </div>

          <div>
            <Label>
              Is admissions staffed 24/7? <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={formData.staffed24_7}
              onValueChange={(value) => setFormData({ ...formData, staffed24_7: value })}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="staffed-yes" />
                <Label htmlFor="staffed-yes">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="staffed-no" />
                <Label htmlFor="staffed-no">No</Label>
              </div>
            </RadioGroup>
            {errors.staffed24_7 && <p className="text-sm text-destructive mt-1">{errors.staffed24_7}</p>}
          </div>

          <div>
            <Label>
              Should ads run outside staffed hours? <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={formData.adsOutsideHours}
              onValueChange={(value) => setFormData({ ...formData, adsOutsideHours: value })}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes-ads-outside" id="yes-ads-outside" />
                <Label htmlFor="yes-ads-outside">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no-ads-outside" id="no-ads-outside" />
                <Label htmlFor="no-ads-outside">No</Label>
              </div>
            </RadioGroup>
            {errors.adsOutsideHours && <p className="text-sm text-destructive mt-1">{errors.adsOutsideHours}</p>}
          </div>
        </section>

        {/* Budget & Success Criteria */}
        <section className="space-y-4">
          <h3 className="text-xl font-semibold border-b pb-2">Budget & Success Criteria</h3>
          
          <div>
            <Label htmlFor="monthlyBudget">
              Monthly Google Ads budget range <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.monthlyBudget}
              onValueChange={(value) => setFormData({ ...formData, monthlyBudget: value })}
            >
              <SelectTrigger className={errors.monthlyBudget ? "border-destructive" : ""}>
                <SelectValue placeholder="Select budget range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5k-10k">$5,000 - $10,000</SelectItem>
                <SelectItem value="10k-20k">$10,000 - $20,000</SelectItem>
                <SelectItem value="20k-35k">$20,000 - $35,000</SelectItem>
                <SelectItem value="35k-50k">$35,000 - $50,000</SelectItem>
                <SelectItem value="50k+">$50,000+</SelectItem>
              </SelectContent>
            </Select>
            {errors.monthlyBudget && <p className="text-sm text-destructive mt-1">{errors.monthlyBudget}</p>}
          </div>

          <div>
            <Label>
              Primary campaign goal <span className="text-destructive">*</span>
            </Label>
            <RadioGroup
              value={formData.primaryGoal}
              onValueChange={(value) => setFormData({ ...formData, primaryGoal: value })}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="more-calls" id="more-calls" />
                <Label htmlFor="more-calls">More calls</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="higher-quality-admissions" id="higher-quality-admissions" />
                <Label htmlFor="higher-quality-admissions">Higher quality admissions</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="insurance-verified-leads" id="insurance-verified-leads" />
                <Label htmlFor="insurance-verified-leads">Insurance-verified leads</Label>
              </div>
            </RadioGroup>
            {errors.primaryGoal && <p className="text-sm text-destructive mt-1">{errors.primaryGoal}</p>}
          </div>

          <div>
            <Label htmlFor="successCriteria">
              What would make this campaign a success in the first 60-90 days? <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="successCriteria"
              value={formData.successCriteria}
              onChange={(e) => setFormData({ ...formData, successCriteria: e.target.value })}
              placeholder="Describe your success metrics (e.g., number of admissions, cost per admission, call quality, etc.)"
              rows={3}
              className={errors.successCriteria ? "border-destructive" : ""}
            />
            {errors.successCriteria && <p className="text-sm text-destructive mt-1">{errors.successCriteria}</p>}
          </div>
        </section>

        {/* Compliance Safeguards */}
        <section className="space-y-4">
          <h3 className="text-xl font-semibold border-b pb-2">Compliance Safeguards</h3>
          
          <div>
            <Label htmlFor="phrasesToAvoid">
              Are there any phrases, claims, or services we should avoid advertising?
            </Label>
            <Textarea
              id="phrasesToAvoid"
              value={formData.phrasesToAvoid}
              onChange={(e) => setFormData({ ...formData, phrasesToAvoid: e.target.value })}
              placeholder="List any specific terms, claims, or approaches we should avoid (optional)"
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="legitScriptStatus">
              LegitScript status <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.legitScriptStatus}
              onValueChange={(value) => setFormData({ ...formData, legitScriptStatus: value })}
            >
              <SelectTrigger className={errors.legitScriptStatus ? "border-destructive" : ""}>
                <SelectValue placeholder="Select LegitScript status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="certified">Certified</SelectItem>
                <SelectItem value="in-progress">In progress</SelectItem>
                <SelectItem value="not-sure">Not sure</SelectItem>
              </SelectContent>
            </Select>
            {errors.legitScriptStatus && <p className="text-sm text-destructive mt-1">{errors.legitScriptStatus}</p>}
          </div>
        </section>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full"
          size="lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
              Submitting Form...
            </>
          ) : (
            "Submit Google Ads Intake Form"
          )}
        </Button>
      </form>
    </div>
  );
}
